

import React, { useEffect } from 'react';
import { Video } from 'lucide-react';
import AdminAppBody from '../../../../components/admin/AdminAppBody';
import useApiCallHooks from '../../../../hooks/useApiCallHooks';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { getUserName } from '../../../../config/user';

ChartJS.register(ArcElement, Tooltip, Legend);

const Home = () => {
  const [response, loading, error, callAPI] = useApiCallHooks();

  useEffect(() => {
    callAPI("get", "mentor/mentor-profile-data");
  }, []);

  const data = response?.data || {};

  const stats = [
    { label: 'Total Student', value: data.totalStudent ?? 0, link: 'View all' },
    {
      label: 'Total Task',
      value: (data.assigned ?? 0) + (data.completed ?? 0) + (data.pending ?? 0),
      sub: `${data.completed ?? 0} completed`
    },
    {
      label: "Total Session's",
      value: data.todaySessionCount ?? 0,
      sub: `${data.completedSessions ?? 0} completed`
    },
    { label: 'Total Assessment', value: data.totalAssessment ?? 0, link: 'View all' },
  ];

  const totalTasks = (data.assigned ?? 0) + (data.completed ?? 0) + (data.pending ?? 0);
  const completedPercent = totalTasks > 0
    ? Math.round(((data.completed ?? 0) / totalTasks) * 100)
    : 0;

  const upcomingSessions = data.todaySessions || [];
  const tasks = data.tasks || [];

  // Chart.js Data
  const chartData = {
    labels: ['Completed', 'Incomplete'],
    datasets: [
      {
        label: 'Tasks',
        data: [data.completed ?? 0, (data.assigned ?? 0) + (data.pending ?? 0)],
        backgroundColor: ['#4facfe', '#a3d5ff'],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Let it resize freely
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 12 } } },
    },
    cutout: '70%',
  };


  return (
    <AdminAppBody
      loading={loading}
      content={
        <div className="p-3 p-md-4">
          <h3 className="mb-4">Welcome {getUserName()}</h3>

          {/* Stats Cards */}
          <div className="row g-3 mb-4">
            {stats.map((item, idx) => (
              <div key={idx} className="col-6 col-md-3">
                <div className="card shadow text-center p-3 border-0 hover-card" style={{ transition: '0.3s' }}>
                  <h4 className="mb-1">{item.value}</h4>
                  <p className="mb-1">{item.label}</p>
                  {item.sub && <small className="text-muted d-block">{item.sub}</small>}
                  {item.link && <div className="text-primary mt-1">{item.link}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Tasks & Graph */}
          <div className="row g-3 mb-4">
            {/* Tasks Table */}
            <div className="col-12 col-lg-6">
              <div className="card shadow h-100 border-0">
                <div className="card-body">
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 fw-bold">Task's</h5>
                    <select className="form-select form-select-sm w-auto mt-2 mt-sm-0">
                      <option>Today's Tasks</option>
                    </select>
                  </div>
                  <div className="table-responsive">
                    <table className="table mb-0">
                      <thead style={{ backgroundColor: '#dceeff' }}>
                        <tr>
                          <th>Task Name</th>
                          <th>Student/Group Name</th>
                          <th>Deadline</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.length > 0 ? (
                          tasks.map((task, idx) => (
                            <tr key={idx}>
                              <td>{task.task?.task || 'N/A'}</td>
                              <td>{task.student?.name || 'N/A'}</td>
                              <td>{task.due_date}</td>
                              <td>{task.status === 1 ? 'Completed' : 'Pending'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">No tasks found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Task Completion Chart */}
  <div className="col-12 col-lg-6">
  <div className="card shadow h-100 border-0">
    <div className="card-body">
      <h5 className="fw-bold mb-3 text-center">Task Completion</h5>

      <div className="row align-items-center">
        {/* Chart Column */}
        <div className="col-12 col-md-6 text-center">
          <div className="chart-container">
            <Doughnut data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Percentage Column */}
        <div className="col-12 col-md-6 text-center">
          <div className="percentage-box">
            <h2 className="percentage-value">{completedPercent}%</h2>
            <p className="chart-subtext">Completed</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>


          </div>

          {/* Upcoming Sessions */}
          <div className="card shadow border-0">
            <div className="card-body">
              <h5 className="mb-3">Upcoming Session</h5>
              <div className="table-responsive">
                <table className="table">
                  <thead className="table-light">
                    <tr>
                      <th>Groups and Student Name</th>
                      <th>Topic</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Meeting Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingSessions.length > 0 ? (
                      upcomingSessions.map((session, idx) => (
                        <tr key={idx}>
                          <td>
                            <img
                              src={`https://api.dicebear.com/7.x/identicon/svg?seed=${session.title}`}
                              alt={session.title}
                              className="rounded-circle me-2"
                              width="24"
                              height="24"
                            />
                            {session.title}
                          </td>
                          <td>{session.topic_id || 'N/A'}</td>
                          <td>{session.start_date}</td>
                          <td>{session.time}</td>
                          <td>
                            <a
                              href={session.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary"
                            >
                              <Video size={16} className="me-1" />
                              <u className="small">Join Link</u>
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">No sessions found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Custom Styles */}
          <style>{`
          .chart-container {
  position: relative;
  width: 100%;
  max-width: 250px; /* Max size for large screens */
  margin: 0 auto;
}

.chart-container canvas {
  width: 100% !important;
  height: auto !important; /* Makes it responsive */
}

.chart-center-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.25rem;
  font-weight: bold;
  color: #007bff;
  text-align: center;
}

.chart-subtext {
  font-size: 0.75rem;
  color: #6c757d;
}
          `}</style>
        </div>
      }
    />
  );
};

export default Home;
